import type { EffortScale } from '@heraion/shared';

import type { ConfirmFn } from '../seams';
import type { EditingTarget } from './LoggerScreen';
import type { LoggerMode } from './loggerMode';
import type { LoggerDispatch } from './state/loggerDraft';
import type { LoggerDraft } from './state/loggerDraftTypes';
import { isEditChanged, isTodayDraftPending } from './state/loggerDraftHelpers';
import { clearPersistedLoggerState } from './state/loggerDraftStorage';

// Discard / close handler (Track R). No-op while a save is in flight. An
// empty draft closes immediately; a non-empty one prompts a destructive
// confirm before resetting + clearing the persisted state. Extracted
// verbatim out of LoggerScreen body. A correction (#327) asks only when the
// save would write something, with its own wording: the session stays saved.
//
// ⛔ The confirm goes through the SEAM, never react-native's Alert — this hook
// lives in packages/ui and `Alert` is a no-op on react-native-web (seams.ts
// states the contract). LG-42, Antenore's report of 2026-08-17: a workout with
// exercises in it could not be closed from the web AT ALL. The ✕ raised the
// native confirm, nothing appeared, and the branch that does the closing lives
// inside its onPress — so the button was simply dead. An EMPTY draft closes on
// the branch above without asking, which is why the first agent-driven run
// (which logged nothing before closing) reported the ✕ as working: the two
// cases take different paths and only the empty one was ever exercised
// (old qa-harness.md §6, emptied 02/10: an empty draft takes another path). Reproduced against the real web build before the fix.

// #327: each correction's draft as it opened, the reducer's own copy, keyed on
// the session id the save uses, once per open. The gate opens before the
// hydrate dispatch lands, so the session's startedAt says which draft it is.
// Dropped while the gate is shut, so a reopen after a save takes a fresh one.
// Module-level because this factory calls no hook (the tests call it directly).
const openedCorrections = new Map<string, LoggerDraft>();

export function useDiscardWorkout({
  submitting,
  draft,
  mode,
  editing,
  effortScale,
  dispatch,
  persistedHydratedRef,
  onClose,
  confirm,
  t,
  stopAllTimers,
}: {
  submitting: boolean;
  draft: LoggerDraft;
  mode: LoggerMode;
  editing: EditingTarget | null;
  effortScale: EffortScale;
  dispatch: LoggerDispatch;
  persistedHydratedRef: { current: boolean };
  onClose: () => void;
  confirm: ConfirmFn;
  t: (k: string, opts?: Record<string, unknown>) => string;
  /** useStopAllTimers: a discarded workout leaves no clock and no bell. */
  stopAllTimers: () => void;
}): () => Promise<boolean> {
  const sessionId = mode === 'log-past' ? (editing?.sessionId ?? null) : null;
  if (sessionId !== null && !persistedHydratedRef.current) {
    openedCorrections.delete(sessionId);
  } else if (
    sessionId !== null &&
    !openedCorrections.has(sessionId) &&
    draft.startedAt === editing?.workout.startedAt
  ) {
    openedCorrections.set(sessionId, draft);
  }
  // ⭐ LG-76 — the clear is AWAITED before onClose, in both branches. Closing is
  // what lets the next workout open, and since LG-76 a plan start READS the slot
  // before it seeds. Left unawaited, that read can land on the draft this close
  // has already ordered gone and resurrect it over the new program.
  const close = async (): Promise<void> => {
    stopAllTimers();
    dispatch({ type: 'reset' });
    await clearPersistedLoggerState();
    persistedHydratedRef.current = false;
    if (sessionId !== null) openedCorrections.delete(sessionId);
    onClose();
  };
  // Resolves true once the workout is closed (LG-76's order: the clear, then
  // onClose), false when nothing closed (a save in flight, a declined confirm),
  // so a caller can open something else only after a real close. The ✕ ignores it.
  return async () => {
    if (submitting) return false;
    const correcting = mode === 'log-past';
    // AP-9b: an unstarted «today» draft holds nothing the athlete did, so it
    // goes without asking, like an empty one. A correction goes when the
    // save would write nothing new; with no opened copy it asks.
    const opened = sessionId !== null ? openedCorrections.get(sessionId) : undefined;
    const nothingToLose = correcting
      ? opened !== undefined && !isEditChanged(opened, draft, effortScale)
      : draft.entries.length === 0 || isTodayDraftPending(draft);
    if (nothingToLose) {
      await close();
      return true;
    }
    // A dismissed dialog resolves false (ConfirmFn's contract), so walking
    // away never discards the workout.
    const ok = await confirm({
      title: t(correcting ? 'mobile.logger.discardEditTitle' : 'mobile.logger.discardTitle'),
      body: t(correcting ? 'mobile.logger.discardEditBody' : 'mobile.logger.discardBody'),
      confirmLabel: t('mobile.logger.discardConfirm'),
      cancelLabel: t(correcting ? 'mobile.logger.discardEditCancel' : 'mobile.logger.discardCancel'),
    });
    if (!ok) return false;
    await close();
    return true;
  };
}
